import { useCallback, useEffect, useState, type DragEvent, type KeyboardEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { uploadAvatarApiV1UsersAvatarUploadPost } from '@/common/api/client/sdk.gen'
import type { UserResponse } from '@/common/api/client/types.gen'
import { useOptionalToast } from '@/common/ui/Toast/toastContext'

const uploadProfileAvatar = (imageFile: File) =>
    uploadAvatarApiV1UsersAvatarUploadPost({
        body: { image_file: imageFile },
    }).then(res => res.data!)

export const PROFILE_AVATAR_MODAL_TOGGLE_ID = 'profileAvatarModal'

export const setProfileAvatarModalOpen = (open: boolean) => {
    const el = document.getElementById(PROFILE_AVATAR_MODAL_TOGGLE_ID) as HTMLInputElement | null
    if (el) {
        el.checked = open
    }
}

export const useProfileAvatar = () => {
    const queryClient = useQueryClient()
    const toast = useOptionalToast()
    const [seedFile, setSeedFile] = useState<File | null>(null)
    const [isDragOver, setIsDragOver] = useState(false)

    useEffect(() => {
        const el = document.getElementById(PROFILE_AVATAR_MODAL_TOGGLE_ID) as HTMLInputElement | null
        if (!el) {
            return
        }
        const handleChange = () => {
            if (!el.checked) {
                setSeedFile(null)
            }
        }
        el.addEventListener('change', handleChange)
        return () => el.removeEventListener('change', handleChange)
    }, [])

    const uploadMutation = useMutation({
        mutationFn: uploadProfileAvatar,
        onSuccess: data => {
            queryClient.setQueryData(['me'], (prev: UserResponse | undefined) =>
                prev ? { ...prev, avatar_url: data.image_url } : prev
            )
            toast?.showToast({ type: 'success', message: 'Фото профиля обновлено' })
            setProfileAvatarModalOpen(false)
        },
        onError: () => {
            toast?.showToast({ type: 'error', message: 'Не удалось загрузить фото' })
        },
    })

    const openModal = useCallback(() => {
        setProfileAvatarModalOpen(true)
    }, [])

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                openModal()
            }
        },
        [openModal]
    )

    const handleDragEnter = useCallback((event: DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        event.stopPropagation()
        const related = event.relatedTarget as Node | null
        if (!related || !event.currentTarget.contains(related)) {
            setIsDragOver(true)
        }
    }, [])

    const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        event.stopPropagation()
        const related = event.relatedTarget as Node | null
        if (!related || !event.currentTarget.contains(related)) {
            setIsDragOver(false)
        }
    }, [])

    const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        event.stopPropagation()
        event.dataTransfer.dropEffect = 'copy'
    }, [])

    const handleDrop = useCallback((event: DragEvent<HTMLDivElement>) => {
        event.preventDefault()
        event.stopPropagation()
        setIsDragOver(false)
        const file = event.dataTransfer.files?.[0]
        if (!file || !file.type.startsWith('image/')) {
            return
        }
        setSeedFile(file)
        setProfileAvatarModalOpen(true)
    }, [])

    const onSeedFileConsumed = useCallback(() => {
        setSeedFile(null)
    }, [])

    const handleExport = useCallback(
        (file: File) => {
            return uploadMutation.mutateAsync(file)
        },
        [uploadMutation]
    )

    return {
        seedFile,
        onSeedFileConsumed,
        isDragOver,
        isUploadPending: uploadMutation.isPending,
        openModal,
        handleKeyDown,
        handleDragEnter,
        handleDragLeave,
        handleDragOver,
        handleDrop,
        handleExport,
    }
}
